import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { hasText } from 'shared/lib/common-util'
import { UserContext } from 'shared/users/contexts/UserContext'
import UserService, { UserListResponse } from '../services/user.service'
import UserModal from './UserModal'
import UsersTableRow from './UsersTableRow'

interface UsersTableProps {
  customerId: number
}

function UsersTable(props: UsersTableProps) {
  const { customerId } = props
  const { t } = useTranslation()
  const [isUserModalVisible, toggleUserModal] = useState<boolean>(false)
  const [usersData, setUsersData] = useState<Array<UserListResponse>>([])
  const [isDirectoColumnVisible, setIsDirectoColumnVisible] = useState(false)

  const {
    user,
  } = useContext(UserContext)

  const hasAnyRemoteStaffCode = (users: UserListResponse[]) => {
    setIsDirectoColumnVisible(users.some((user) => hasText(user.remoteStaffCode)))
  }

  const getAllUsers = () => {
    UserService.getAllUsersForCurrentCustomer()
      .then((users) => {
        // eslint-disable-next-line no-nested-ternary
        users.sort((a, b) => ((a.id < b.id) ? -1 : ((a.id > b.id) ? 1 : 0)))
        setUsersData(users)
        hasAnyRemoteStaffCode(users)
      })
      .catch(() => toast.error(t('messages.customerUsersError')))
  }

  const loadUsersForCustomer = () => {
    if (user?.role === 'ADMIN') {
      if (customerId === undefined) {
        return
      }
      UserService.getAllUsersForCustomer(customerId)
        .then((users) => {
          // eslint-disable-next-line no-nested-ternary
          users.sort((a, b) => ((a.id < b.id) ? -1 : ((a.id > b.id) ? 1 : 0)))
          setUsersData(users)
          hasAnyRemoteStaffCode(users)
        })
        .catch(() => toast.error(t('messages.customerUsersError')))
    } else if (user?.role === 'CUSTOMER_ADMIN') {
      getAllUsers()
    }
  }

  useEffect(() => {
    loadUsersForCustomer()
  }, [customerId, isUserModalVisible])

  return (
    <div>
      <div className="position-relative heading">
        <h3>
          {t('customers.users')}
        </h3>
        {
          customerId
          && (
            <div className="heading-button d-flex align-items-center">
              <button
                type="button"
                className="btn btn-outline-dark btn-sm d-flex align-items-center"
                onClick={() => toggleUserModal(true)}
              >
                <i className="far fa-plus me-1" />
                <span>{t('buttons.addUser')}</span>
              </button>
            </div>
          )
        }
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-hover table-borderless data-table">
          <thead>
            <tr>
              <th key="Name">
                <div>
                  {t('common.name', { context: 'capitalized' })}
                </div>
              </th>
              <th key="E-mail">
                <div>
                  {t('common.email')}
                </div>
              </th>
              {
                user?.role === 'ADMIN' && isDirectoColumnVisible && (
                  <th key="remoteStaffCode">
                    <div>
                      {t('directo.remoteStaffCode')}
                    </div>
                  </th>
                )
              }
              <th key="Phone">
                <div>
                  {t('common.phone')}
                </div>
              </th>
              <th key="Status">
                <div>
                  {t('transit.status')}
                </div>
              </th>
              <th key="Created">
                <div>
                  {t('declaration.created')}
                </div>
              </th>
              <th key="Deleted">
                <div>
                  {t('customers.deleted')}
                </div>
              </th>
              <th key="lastLogin">
                <div>
                  {t('customers.lastLogin')}
                </div>
              </th>
              <th key="ActionsColumn">
                <div key="pl" />
              </th>
            </tr>
          </thead>
          <tbody>
            {usersData.map((userResponse) => (
              <UsersTableRow
                key={`${userResponse.email}_row`}
                currentUser={userResponse}
                loadUsers={loadUsersForCustomer}
                currentUserEmail={user?.email ?? '-'}
                customerId={customerId}
                isDirectoColumnVisible={isDirectoColumnVisible}
              />
            ))}
          </tbody>
        </table>
      </div>
      <UserModal
        isUserModalVisible={isUserModalVisible}
        toggleUserModal={toggleUserModal}
        customerId={customerId}
      />
    </div>
  )
}

export default UsersTable
